import { AudioProvider, LoadingPage, NetworkErrorPage, useTheme } from '@lib/common';
import 'antd/dist/antd.css';
import { ConfigProvider } from 'antd';
import locale from 'antd/lib/locale/en_US';
import localeVi from 'antd/lib/locale/vi_VN';
import { routeManager } from '@lib/route-manager';
import { ColorModeProvider, ThemeProvider } from '@xstyled/styled-components';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import { appWithTranslation } from 'next-i18next';
import type { NextPage } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import React, { memo, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { bootstrap } from '../bootstrap';
import { useMicrosoftClarity } from '../hooks/use-microsoft-clarity.hook';
import { runAsyncEpics } from '../redux/root.epic';
import { resetReduxStateOnRouteChange } from '../redux/root.reducer';
import { wrapper } from '../redux/store';
import {
  buildAuthenticationRedirectUrl,
  getAccessToken,
  hasValidAccessToken,
  isProtectedTestMakerPath,
} from '../utils/auth-guard.util';

const Analytics = dynamic(() => import('@vercel/analytics/react').then((mod) => mod.Analytics), {
  ssr: false,
});

const LocaleWrapper = memo(({ children }: { children: ReactNode }) => {
  const { locale: currentLocale = 'en' } = useRouter();
  const antdLocale = currentLocale === 'vi' ? localeVi : locale;

  return <ConfigProvider locale={antdLocale}>{children}</ConfigProvider>;
});

LocaleWrapper.displayName = 'LocaleWrapper';

type Page<P = Record<string, unknown>> = NextPage<P> & {
  getLayout?: (page: ReactNode) => ReactNode;
};

type Props = AppProps & {
  Component: Page;
};

type AppPageContentProps = {
  Component: Page;
  pageProps: AppProps['pageProps'];
  getLayout: (page: ReactNode) => ReactNode;
  theme: ReturnType<typeof useTheme>['theme'];
};

const AppPageContent = memo(({ Component, pageProps, getLayout, theme }: AppPageContentProps) => {
  const page = useMemo(
    () => getLayout(<Component {...pageProps} />),
    [Component, getLayout, pageProps],
  );

  return (
    <>
      <Head>
        <link rel="shortcut icon" href="/images/Favicon.svg" />
      </Head>
      <LocaleWrapper>
        <ThemeProvider theme={theme}>
          <ColorModeProvider>
            <AudioProvider>{page}</AudioProvider>
          </ColorModeProvider>
        </ThemeProvider>
      </LocaleWrapper>
      <Analytics />
      <Script id="chatwoot-settings" strategy="beforeInteractive">
        {`window.chatwootSettings = {"position":"right","type":"standard","launcherTitle":""};`}
      </Script>
      <Script
        id="chatwoot-sdk"
        src="https://app.chatwoot.com/packs/js/sdk.js"
        strategy="afterInteractive"
        onLoad={() => {
          window.chatwootSDK?.run({
            websiteToken: 'wrajxMyS8LatHqJofqw7JDKf',
            baseUrl: 'https://app.chatwoot.com',
          });
        }}
      />
    </>
  );
});

AppPageContent.displayName = 'AppPageContent';

function MyApp({ Component, pageProps }: Props) {
  const store = useStore();
  const router = useRouter();
  const { isReady, locale, events, asPath, push } = router;
  const [isOnline, setIsOnline] = useState(true);
  const [isLocaleInitialized, setIsLocaleInitialized] = useState(false);
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const hasInitializedRef = useRef(false);
  const { theme } = useTheme();

  const getLayout = useMemo(() => Component.getLayout ?? ((page: ReactNode) => page), [Component]);

  useEffect(() => {
    bootstrap();
  }, []);

  useMicrosoftClarity();

  useEffect(() => {
    // Shallow updates (e.g. explore modal ?formId=) must not toggle the full-page loader:
    // that would unmount page content and remount Explore, re-running data effects.
    const handleRouteChangeStart = (_url: string, { shallow }: { shallow?: boolean } = {}) => {
      if (!shallow) {
        setIsRouteLoading((prev) => (prev ? prev : true));
      }
    };
    const handleRouteChangeComplete = (_url: string, { shallow }: { shallow: boolean }) => {
      setIsRouteLoading((prev) => (prev ? false : prev));
      if (!shallow) {
        store.dispatch(resetReduxStateOnRouteChange());
      }
    };
    const handleRouteChangeError = () => setIsRouteLoading((prev) => (prev ? false : prev));

    events.on('routeChangeStart', handleRouteChangeStart);
    events.on('routeChangeComplete', handleRouteChangeComplete);
    events.on('routeChangeError', handleRouteChangeError);

    return () => {
      events.off('routeChangeStart', handleRouteChangeStart);
      events.off('routeChangeComplete', handleRouteChangeComplete);
      events.off('routeChangeError', handleRouteChangeError);
    };
  }, [events, store]);

  useEffect(() => {
    if (!isReady || hasInitializedRef.current) {
      return;
    }

    let cancelled = false;

    const initializeApp = async () => {
      if (typeof window === 'undefined') {
        return;
      }

      const { publicRuntimeConfig } = getConfig();
      const accessToken = getAccessToken();
      const savedLocale = localStorage.getItem('test_maker_locale') || 'en';

      if (locale !== savedLocale) {
        push(asPath, asPath, { locale: savedLocale });
        return;
      }

      hasInitializedRef.current = true;

      const isProtectedPath = isProtectedTestMakerPath(asPath);
      if (isProtectedPath) {
        const signInUrl = buildAuthenticationRedirectUrl(asPath);
        const shouldRedirect = !hasValidAccessToken() && !asPath.includes('callback');

        if (shouldRedirect) {
          window.location.href = signInUrl;
          return;
        }
      }

      try {
        await runAsyncEpics({
          accessToken,
          formBaseUrl: publicRuntimeConfig.api.baseFormUrl,
          assignmentBaseUrl: publicRuntimeConfig.api.baseAssignmentUrl,
          classBaseUrl: publicRuntimeConfig.api.baseClassUrl,
          fileStorageBaseUrl: publicRuntimeConfig.api.baseFileStorageUrl,
          reportBaseUrl: publicRuntimeConfig.api.baseAssignmentUrl,
          userBaseUrl: publicRuntimeConfig.api.baseIdentityUrl,
          aiBaseUrl: publicRuntimeConfig.api.baseAiUrl,
          accountingBaseUrl: publicRuntimeConfig.api.accountingBaseUrl,
          appSyncConfig: {
            url: publicRuntimeConfig.appSync.url,
            apiKey: publicRuntimeConfig.appSync.apiKey,
          },
          timeout: publicRuntimeConfig.api.timeout,
        });
      } catch {
        // Epic initialization failed; app still renders with degraded API state.
      }

      if (!cancelled) {
        setIsOnline(navigator.onLine);
        setIsLocaleInitialized(true);
      }
    };

    void initializeApp();

    return () => {
      cancelled = true;
    };
  }, [isReady, locale, asPath, push]);

  const isAppReady = useMemo(
    () =>
      routeManager.isReady &&
      typeof window !== 'undefined' &&
      isLocaleInitialized &&
      !isRouteLoading,
    [isLocaleInitialized, isRouteLoading],
  );

  if (!isOnline) {
    return <NetworkErrorPage />;
  }

  if (!isAppReady) {
    return <LoadingPage title={router.locale === 'vi' ? 'Đang tải...' : 'Loading...'} />;
  }

  return (
    <AppPageContent
      Component={Component}
      pageProps={pageProps}
      getLayout={getLayout}
      theme={theme}
    />
  );
}

const MyAppTranslationWrapper = appWithTranslation(MyApp);
export default wrapper.withRedux(MyAppTranslationWrapper);
